import { OutlinedTextFieldProps } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { FormField } from 'src/components';
import { TODAY_DATE } from 'src/components/AddComplimentaryPeriodModal/const';
import { FormDatePickerProps } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { PartnerGroupRules, usePartnerGroupRules } from 'src/graphql';
import { Modals, useModalState } from 'src/store/modals';
import { WARRANTY_LENGTH_OPTIONS } from './util';

export interface ProposalWonFormValues {
  warrantyLength: { id: string; label: string; value: number };
  estimatedDay1Date?: Dayjs | null;
  sendEducationalEmails: boolean;
  tosAccepted: boolean;
  hasPreSoldMembership: boolean;
  orgNotes?: string;
  membershipSalesNotes?: string;
}

export function useProposalWonForm() {
  const { modalProps } = useModalState(Modals.ProposalWon);
  const { partnerGroupRules } = usePartnerGroupRules(modalProps?.address?.partnerId);

  const hasAccessToPSM = partnerGroupRules[PartnerGroupRules.AccessToPresoldMemberships];
  const hasAccessToSubManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];

  const alreadyHasPreSoldMembership = useMemo(() => {
    const psm = modalProps?.address?.preSoldMemberships?.[0];
    return Boolean(psm?.id);
  }, [modalProps?.address?.preSoldMemberships]);

  const proposalWonFormFields = useMemo<FormField[]>(() => {
    const commonProps: {
      textFieldProps: OutlinedTextFieldProps;
      datePickerProps: FormDatePickerProps['datePickerProps'];
    } = {
      textFieldProps: {
        variant: 'outlined' as const,
        size: 'small',
        sx: {
          marginTop: (theme) => theme.spacing(1),
        },
      },
      datePickerProps: {
        slotProps: {
          textField: {
            size: 'small',
          },
        },
      },
    };

    return [
      {
        type: 'autocomplete' as const,
        name: 'warrantyLength',
        label: 'Warranty Length',
        options: WARRANTY_LENGTH_OPTIONS,
        defaultValue: WARRANTY_LENGTH_OPTIONS[0],
        textFieldProps: commonProps.textFieldProps,
      },
      {
        type: 'datepicker' as const,
        name: 'estimatedDay1Date',
        label: 'Estimated Day1 Date',
        value: null,
        defaultValue: dayjs(modalProps?.project?.estimatedDay1Date || TODAY_DATE),
        required: 'Estimated Day1 Date is required',
        datePickerProps: commonProps.datePickerProps,
      },
      {
        type: 'switch' as const,
        name: 'tosAccepted',
        label: 'Terms of Service Accepted',
        defaultValue: modalProps?.address?.tosAccepted === 'Yes',
      },
      {
        type: 'switch' as const,
        name: 'sendEducationalEmails',
        label: 'Send Educational Emails',
        defaultValue: false,
        hidden: !hasAccessToSubManagementPlus,
      },
      {
        type: 'switch' as const,
        name: 'hasPreSoldMembership',
        label: 'Did you "Pre-Sell" a membership?',
        defaultValue: false,
        hidden: !hasAccessToPSM || alreadyHasPreSoldMembership,
      },
      {
        type: 'rich-text-bubble' as const,
        name: 'orgNotes',
        label: 'Site Notes',
        defaultValue: modalProps?.address?.orgNotes || '',
      },
      {
        type: 'rich-text-bubble' as const,
        name: 'membershipSalesNotes',
        label: 'Membership Sales Notes',
        defaultValue: '',
      },
    ];
  }, [
    alreadyHasPreSoldMembership,
    hasAccessToPSM,
    hasAccessToSubManagementPlus,
    modalProps?.address?.orgNotes,
    modalProps?.address?.tosAccepted,
    modalProps?.project?.estimatedDay1Date,
  ]);

  return { proposalWonFormFields };
}
